import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';

import { Command } from '@air/cmdk';
import { Search as SearchIcon } from '@air/next-icons';
import { cn } from '@air/utils-tailwind';

import {
  SearchCommandFilterCount,
  type SearchCommandFilterCountProps,
} from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandInputPlaceholder } from '~/components/SearchCommand/SearchCommandInputPlaceholder';

export type SearchCommandInputProps = Pick<
  ComponentPropsWithoutRef<typeof Command.Input>,
  'className' | 'defaultValue' | 'onValueChange' | 'placeholder' | 'value'
> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    compact?: boolean;
  };

export const SearchCommandInput = memo(
  forwardRef<HTMLInputElement, SearchCommandInputProps>(
    ({ className, compact, count, defaultValue, onValueChange, placeholder, value }: SearchCommandInputProps, ref) => {
      return (
        <div
          className={cn(
            'relative -m-px flex shrink-0 gap-1 overflow-hidden rounded-t-4 border border-transparent bg-grey-1',
            compact ? 'h-8' : 'h-10',
            className,
          )}
          data-testid="SEARCH_COMMAND_INPUT"
        >
          <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-grey-9" />
          <Command.Input
            className={cn(
              'size-full rounded-8 bg-transparent pl-9 font-medium text-grey-12 outline-none',
              compact ? 'pr-10 text-14 placeholder:text-transparent' : 'text-16 placeholder:text-grey-9',
            )}
            data-testid="SEARCH_COMMAND_INPUT_INPUT"
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            placeholder={placeholder}
            value={value}
            ref={ref}
          />
          {compact && !value && placeholder && (
            <SearchCommandInputPlaceholder className="left-9" placeholder={placeholder} />
          )}
          {!!count && <SearchCommandFilterCount count={count} />}
        </div>
      );
    },
  ),
);

SearchCommandInput.displayName = 'SearchCommandInput';
